import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { useTranslation } from 'react-i18next';

function TeamMemberTaskPage() {
  const { jdId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [jobDescription, setJobDescription] = useState(null);
  const [teamCultureText, setTeamCultureText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const fetchJobDescription = async () => {
      if (!jdId) {
        setLoading(false);
        setError(t('teamMemberTaskPage.errorNoJdId'));
        return;
      }
      try {
        const user = auth.currentUser;
        if (!user) {
          setError(t('teamMemberTaskPage.errorNotLoggedIn'));
          setLoading(false);
          return;
        }

        const docRef = doc(db, 'jobDescriptions', jdId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setJobDescription(data);
          
          const submissions = data.teamCultureSubmissions || [];
          const userSubmission = submissions.find(sub => sub.submittedBy === user.uid);

          if (userSubmission) {
            setTeamCultureText(userSubmission.plainText);
            setIsSubmitted(true);
          }
        } else {
          setError(t('teamMemberTaskPage.errorNotFound'));
        }
      } catch (err) {
        setError(t('teamMemberTaskPage.errorFetch'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobDescription();
  }, [jdId, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamCultureText.trim()) {
      setError(t('teamMemberTaskPage.errorEmptyInput'));
      return;
    }
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error(t('teamMemberTaskPage.errorNotLoggedIn'));
      }
      const idToken = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/processTeamCulture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          jdId: jdId,
          plainText: teamCultureText,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('teamMemberTaskPage.errorSubmit'));
      }

      setSubmitSuccess(true);
      setTimeout(() => navigate('/welcome'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="li-layout"><div style={{ margin: 'auto' }}>{t('loading')}</div></div>;
  }

  if (error && !jobDescription) {
    return <div className="li-layout"><div style={{ margin: 'auto', color: 'red' }}>Error: {error}</div></div>;
  }

  return (
    <div style={{ maxWidth: '780px', margin: '0 auto', padding: '24px 16px' }}>
      
      <div className="li-card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #e0e0e0', paddingBottom: '16px' }}>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>{t('teamMemberTaskPage.title', { title: jobDescription?.title })}</h1>
            <span style={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)' }}>Culture Fit Assessment</span>
          </div>
          <Link to="/welcome" className="li-btn-outline-secondary" style={{ padding: '6px 12px', fontSize: '14px' }}>← {t('backToHome')}</Link>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>{t('teamMemberTaskPage.jobDetails')}</h3>
          <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.9)', lineHeight: '1.5', backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px' }}>
            {jobDescription?.summary || t('teamMemberTaskPage.noSummary')}
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
            {isSubmitted ? t('teamMemberTaskPage.yourSubmittedInputTitle') : t('teamMemberTaskPage.yourInputTitle')}
          </h3>
          <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)', marginBottom: '16px' }}>
            {isSubmitted 
              ? t('teamMemberTaskPage.submittedMessage')
              : t('teamMemberTaskPage.instruction')
            }
          </p>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <textarea
                value={teamCultureText}
                onChange={(e) => setTeamCultureText(e.target.value)}
                rows="10"
                placeholder={t('teamMemberTaskPage.placeholder')}
                style={{ 
                  width: '100%', 
                  fontFamily: 'inherit', 
                  border: '1px solid rgba(0,0,0,0.6)', 
                  borderRadius: '4px', 
                  padding: '12px',
                  fontSize: '14px',
                  backgroundColor: isSubmitted ? '#f3f2ef' : 'white',
                  resize: 'vertical'
                }}
                disabled={isSubmitted}
              />
            </div>
            
            <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              {!isSubmitted && (
                <button type="submit" className="li-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? t('teamMemberTaskPage.submittingButton') : t('teamMemberTaskPage.submitButton')}
                </button>
              )}
              {submitSuccess && <span style={{ color: '#057642', fontWeight: '600', fontSize: '14px' }}>{t('teamMemberTaskPage.submitSuccess')}</span>}
              {error && <span style={{ color: '#cc1016', fontWeight: '600', fontSize: '14px' }}>Error: {error}</span>}
            </div>
          </form>
        </div>
      </div>
      
      <div className="li-footer-links" style={{ marginTop: '24px' }}>
         <Link to="/about">{t('footer.about')}</Link>
         <Link to="/accessibility">{t('footer.accessibility')}</Link>
         <Link to="/help-center">{t('footer.helpCenter')}</Link>
         <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
         <span style={{ display: 'block', marginTop: '8px' }}>{t('auth.footer.rights')}</span>
      </div>
    </div>
  );
}

export default TeamMemberTaskPage;